import { useState, useEffect } from 'react';
import BackWithSidebar from '../../../../components/BackWithSidebar';
import GeneralIcon from '../../../../assets/index';
import { saveSchedules, loadDraftSchedules } from '../../../../models/horarios';
import { useToast } from '../../../../components/ToastContext';

const WEEK_DAYS = [
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
  'Domingo',
];

function formatTime(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function overlaps(existing, from, to) {
  // existing: {from, to} in minutes
  return !(to <= existing.from || from >= existing.to);
}

export default function Horarios() {
  const [schedules, setSchedules] = useState(() => ({}));

  const [showAdd, setShowAdd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedDay, setSelectedDay] = useState(WEEK_DAYS[0]);
  const [editIndex, setEditIndex] = useState(null);
  const [fromRaw, setFromRaw] = useState('');
  const [toRaw, setToRaw] = useState('');
  const [error, setError] = useState('');
  const [toDelete, setToDelete] = useState(null);

  const toast = useToast();

  useEffect(() => {
    try {
      const draft = loadDraftSchedules();
      if (draft) {
        setSchedules(draft);
        // informational only
        toast.showInfo && toast.showInfo('Rascunho de horários carregado localmente.');
        return;
      }
      const saved = window.localStorage.getItem('horarios');
      if (saved) {
        setSchedules(JSON.parse(saved));
      }
    } catch (e) {
      // ignore
    }
  }, [toast]);

  const formatInput = (raw) => {
    const r = String(raw || '').replace(/\D/g, '');
    if (!r) return '';
    if (r.length <= 2) return r;
    return r.slice(0, 2) + ':' + r.slice(2);
  };

  const handleFromChange = (val) => {
    const digits = String(val || '').replace(/\D/g, '').slice(0, 4);
    setFromRaw(digits);
  };

  const handleToChange = (val) => {
    const digits = String(val || '').replace(/\D/g, '').slice(0, 4);
    setToRaw(digits);
  };

  const openAdd = (day) => {
    setSelectedDay(day || WEEK_DAYS[0]);
    setFromRaw('');
    setToRaw('');
    setEditIndex(null);
    setError('');
    setShowAdd(true);
  };

  const submitAdd = async () => {
    setError('');
    const rf = String(fromRaw).replace(/\D/g, '');
    const rt = String(toRaw).replace(/\D/g, '');
    if (rf.length !== 4 || rt.length !== 4) {
      setError('Informe horários no formato HHMM (ex: 0830).');
      return;
    }
    const fromH = parseInt(rf.slice(0, 2), 10);
    const fromM = parseInt(rf.slice(2, 4), 10);
    const toH = parseInt(rt.slice(0, 2), 10);
    const toM = parseInt(rt.slice(2, 4), 10);
    if (Number.isNaN(fromH) || Number.isNaN(fromM) || Number.isNaN(toH) || Number.isNaN(toM)) {
      setError('Preencha horários válidos antes de salvar.');
      return;
    }
    if (fromM < 0 || fromM > 59 || toM < 0 || toM > 59) {
      setError('Os minutos devem estar entre 00 e 59.');
      return;
    }
    if (fromH < 0 || fromH > 23 || toH < 0 || toH > 24) {
      setError('Horários devem estar entre 00:00 e 24:00.');
      return;
    }
    if (toH === 24 && toM !== 0) {
      setError('Se usar 24 como hora, os minutos devem ser 00 (2400).');
      return;
    }
    const fromTotal = fromH * 60 + fromM;
    const toTotal = toH * 60 + toM;
    if (fromTotal >= toTotal) {
      setError('Horário de abertura deve ser menor que o de fechamento.');
      return;
    }

    const list = schedules[selectedDay] || [];
    const conflict = list.some((it, idx) => {
      if (editIndex !== null && idx === editIndex) return false;
      return overlaps(it, fromTotal, toTotal);
    });
    if (conflict) {
      setError('Este horário sobrepõe um horário já existente neste dia.');
      return;
    }

    const updated = { ...schedules };
    if (editIndex !== null) {
      updated[selectedDay] = [...(updated[selectedDay] || [])];
      updated[selectedDay][editIndex] = { from: fromTotal, to: toTotal };
    } else {
      updated[selectedDay] = [...(updated[selectedDay] || []), { from: fromTotal, to: toTotal }];
    }

    setSchedules(updated);

    // persist local copy immediately so user doesn't lose data
    try {
      window.localStorage.setItem('horarios', JSON.stringify(updated));
    } catch (e) {
      console.warn('failed to persist horarios locally', e);
    }

    try {
      const res = await saveSchedules(updated);
      // if saveSchedules returned an object indicating saved:false, keep draft and inform
      if (res && res.saved === false) {
        toast.showInfo('Rascunho salvo localmente — será reenviado quando possível.');
      } else {
        // success: remove draft placeholder
        try {
          window.localStorage.removeItem('horariosDraft');
        } catch (e) {}
        toast.showSuccess('Horário salvo com sucesso.');
      }
    } catch (err) {
      console.debug('saveSchedules failed', err);
      toast.showError('Falha ao salvar horário.');
    }

    setShowAdd(false);
  };

  const confirmDelete = (day, idx) => {
    setToDelete({ day, idx });
    setShowConfirm(true);
  };

  const doDelete = async () => {
    if (!toDelete) return;
    const updated = { ...schedules };
    updated[toDelete.day] = updated[toDelete.day].filter((_, i) => i !== toDelete.idx);
    setSchedules(updated);

    try {
      window.localStorage.setItem('horarios', JSON.stringify(updated));
    } catch (e) {
      console.warn('failed to persist horarios locally', e);
    }

    try {
      const res = await saveSchedules(updated);
      if (res && res.saved === false) {
        toast.showInfo('Rascunho salvo localmente — será reenviado quando possível.');
      } else {
        try { window.localStorage.removeItem('horariosDraft'); } catch (e) {}
        toast.showSuccess('Horário excluído.');
      }
    } catch (err) {
      console.debug('saveSchedules failed', err);
      toast.showError('Falha ao excluir horário.');
    }

    setShowConfirm(false);
    setToDelete(null);
  };

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <BackWithSidebar />
          <h1 className="text-lg font-semibold">Horários de funcionamento</h1>
        </div>

        {WEEK_DAYS.map((day) => {
          const list = schedules[day] || [];
          if (!list.length) return null; // only show days that have schedules
          return (
            <div key={day} className="mb-6">
              <h3 className="text-sm font-medium mb-3">{day}</h3>
              <div className="space-y-3">
                {list.map((s, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white border rounded shadow-sm">
                    <div className="text-sm text-gray-700">Das {formatTime(s.from)} até às {formatTime(s.to)}</div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const fh = Math.floor(s.from / 60);
                          const fm = s.from % 60;
                          const th = Math.floor(s.to / 60);
                          const tm = s.to % 60;
                          setSelectedDay(day);
                          setEditIndex(idx);
                          setFromRaw(String(fh).padStart(2, '0') + String(fm).padStart(2, '0'));
                          setToRaw(String(th).padStart(2, '0') + String(tm).padStart(2, '0'));
                          setShowAdd(true);
                        }}
                        className="p-2 rounded-md hover:bg-gray-50 text-gray-600"
                      >
                        <GeneralIcon iconName="editar" className="w-5 h-5" />
                      </button>
                      <button onClick={() => confirmDelete(day, idx)} className="p-2 rounded-md bg-red-50 text-red-600"> <GeneralIcon iconName="lixeira" className="w-5 h-5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Global add button fixed at bottom */}
        <div className="fixed left-0 right-0 bottom-4 flex justify-center pointer-events-none">
          <div className="w-full max-w-lg px-4 pointer-events-auto">
            <button onClick={() => openAdd()} className="w-full inline-flex items-center justify-center gap-2 py-3 bg-purple-600 text-white rounded shadow-lg">
              <span className="text-lg">+</span>
              <span className="text-sm">Adicionar um novo horário</span>
            </button>
          </div>
        </div>

        {/* Add/Edit modal (simple inline modal) */}
        {showAdd && (
          <div className="fixed inset-0 flex items-end md:items-center justify-center z-50">
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowAdd(false)} />
            <div className="relative w-full max-w-md bg-white rounded-t-xl md:rounded-xl p-6">
              <h2 className="mb-4 font-semibold">Adicionar horário</h2>
              <div className="mb-3 text-sm text-purple-600 bg-purple-50 p-3 rounded flex gap-3 items-start">
                <div className="w-10 flex-shrink-0 text-center">
                  <GeneralIcon iconName="dica" alt="dica" className="w-8 h-8 mx-auto" />
                  <strong className="block">DICA</strong>
                </div>
                <div>
                  <div>Você pode excluir o horário padrão de um dia e adicionar vários horários diferentes para o mesmo dia.</div>
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-sm mb-1">Dia da semana*</label>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {WEEK_DAYS.map((d) => (
                    <label key={d} className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${selectedDay === d ? 'ring-2 ring-purple-300' : ''}`}>
                      <input type="radio" name="dia" checked={selectedDay === d} onChange={() => setSelectedDay(d)} className="sr-only" />
                      <span className={`w-4 h-4 rounded ${selectedDay === d ? 'bg-purple-600' : 'bg-gray-100'}`} />
                      <span className="text-sm">{d}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-sm mb-1">Horário de abertura* (HHMM)</label>
                <input inputMode="numeric" pattern="[0-9]*" type="text" value={formatInput(fromRaw)} onChange={(e) => handleFromChange(e.target.value)} placeholder="ex: 0830" className="w-full p-2 border rounded" />
              </div>

              <div className="mb-3">
                <label className="block text-sm mb-1">Horário de fechamento* (HHMM)</label>
                <input inputMode="numeric" pattern="[0-9]*" type="text" value={formatInput(toRaw)} onChange={(e) => handleToChange(e.target.value)} placeholder="ex: 2200" className="w-full p-2 border rounded" />
              </div>

              {error && <div className="text-sm text-red-500 mb-3">{error}</div>}

              <div className="flex gap-2">
                <button onClick={() => setShowAdd(false)} className="flex-1 py-2 border rounded">Cancelar</button>
                <button onClick={submitAdd} className="flex-1 py-2 bg-purple-600 text-white rounded">{editIndex !== null ? 'Salvar' : 'Adicionar'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {showConfirm && (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowConfirm(false)} />
            <div className="relative w-full max-w-xs bg-white rounded p-4">
              <h3 className="font-semibold mb-2">Excluir horário</h3>
              <p className="text-sm text-gray-700 mb-4">Você tem certeza que deseja excluir este horário?</p>
              <div className="flex gap-2">
                <button onClick={() => setShowConfirm(false)} className="flex-1 py-2 border rounded">Cancelar</button>
                <button onClick={doDelete} className="flex-1 py-2 bg-red-100 text-red-600 rounded">Excluir</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
